// @ts-ignore
import videojs from "video.js";
import 'videojs-youtube'; // Registers the YouTube tech

import { AccessTime as AccessTimeIcon, SaveAlt as SaveAltIcon } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";
import { apiRequest } from "api-client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { formatSeconds } from "utils/time";
import "video.js/dist/video-js.css";


const VideoPreview = ({ url, duration, videoSrcUrl }: { url?: string, duration?: number, videoSrcUrl?: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [start, setStart] = useState(formatSeconds(0));
  const [end, setEnd] = useState(formatSeconds(duration || 0));
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (videoRef.current) {
      videojs(videoRef.current);
    }
  }, [videoRef.current]);

  const onDownload = useCallback(async () => {
    try {
      if (start > end) {
        toast("Start is greater than end.")
        return;
      }
      setLoading(true);
      const response = await apiRequest.post(
        `/videos/download-clip`,
        {
          url,
          start,
          end,
        },
        {
          responseType: 'blob', // 🔑 tells Axios to expect binary
        }
      );

      // Create a download link
      const blob = new Blob([response.data], { type: 'video/mp4' });
      const fileUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', 'clip.mp4'); // set filename
      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(fileUrl);
      setLoading(false);
    } catch (error) {
      console.error('Failed to download clip', error);
      toast('Failed to download clip');
      setLoading(false);
    }
  }, [url, start, end]);

  return (
    <>
      <Box className="video-wrapper">
        <video className="video-js" ref={videoRef} controls>
          <source src={url} type={getVideoType(url)} />
        </video>
      </Box>

      {url?.endsWith('playlist.m3u8') &&
        <Box mt={2}>
          <Typography fontWeight={500} mb={1}>Extract a short clip</Typography>
          <Box display="flex">
            <FormControl sx={{mr: 1}} size="small">
              <InputLabel htmlFor="start-input">Start</InputLabel>
              <OutlinedInput
                id="start-input"
                label="Start"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setStart(formatSeconds(videoRef.current?.currentTime || ''))}
                    >
                      <AccessTimeIcon />
                    </IconButton>
                  </InputAdornment>
                } />
            </FormControl>
            <FormControl size="small">
              <InputLabel htmlFor="end-input">End</InputLabel>
              <OutlinedInput
                label="End"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setEnd(formatSeconds(videoRef.current?.currentTime || ''))}
                    >
                      <AccessTimeIcon />
                    </IconButton>
                  </InputAdornment>
                } />
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="space-between" mt={2}>
            <LoadingButton onClick={onDownload} variant="outlined" size="small" loading={loading}>Extract</LoadingButton>
            {videoSrcUrl &&
              <IconButton component="a" href={videoSrcUrl} target="_blank" rel="noopener noreferrer" title="Download original video">
                <SaveAltIcon />
              </IconButton>
            }
          </Box>
        </Box>
      }
    </>
  )
}

export default VideoPreview;

const getVideoType = (url?: string | null) => {
  if (typeof url !== "string") return "";
  if (url.includes("playlist.m3u8")) {
    return "application/x-mpegURL";
  }

  if (url.includes("https://vimeo.com")) {
    return "video/vimeo";
  }

  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return "video/youtube";
  }

  if (url.endsWith(".mp4")) {
    return "video/mp4";
  }
  return "";
};